/* Structured property-inspection form: constants, shape, helpers and requirement checks (mirrors backend/visit_rules.py). */
export const MIN_PROPERTY_PHOTOS = 9;
export const MIN_LOAN_LEADS = 4;

export const PHOTO_TAGS = [
  { value: "front", label: "Front View" },
  { value: "road", label: "Road View" },
  { value: "boundaries", label: "Boundaries" },
  { value: "interior", label: "Interior" },
  { value: "roof", label: "Roof" },
  { value: "surrounding", label: "Surrounding Area" },
  { value: "other", label: "Other" },
];
export const PROPERTY_TYPES = ["Residential Plot", "Residential House / Villa", "Flat / Apartment", "Commercial Shop / Office", "Industrial", "Agricultural Land", "Other"];
export const CONSTRUCTION_TYPES = ["RCC", "Load-bearing", "Semi-permanent", "Kutcha", "Open plot", "Other"];
export const LOCAL_BODY_TYPES = [
  { value: "panchayat", label: "Panchayat" },
  { value: "nagar_parishad", label: "Nagar Parishad" },
  { value: "nagar_palika", label: "Nagar Palika" },
  { value: "nagar_nigam", label: "Nagar Nigam" },
];
export const LAND_RATE_SOURCES = [
  { value: "broker", label: "Property Broker" },
  { value: "local_reference", label: "Local Reference" },
];
export const STEPS = [
  { key: "basic", label: "Basic Details" },
  { key: "neighbor", label: "Neighbor & Local Body" },
  { key: "land_rate", label: "Land Rate" },
  { key: "khasra", label: "Khasra Milan" },
  { key: "leads", label: "Loan Leads" },
  { key: "photos", label: "Property Photos" },
];
export const SECTION_PATHS = {
  property: "property_photos",
  neighbor: "neighbor.neighbor_photo_ids",
  local_body: "neighbor.local_body_photo_ids",
  land_rate: "land_rate.photo_ids",
  khasra: "khasra_photo_ids",
};
export const SECTION_LABELS = {
  property: "Property photo", neighbor: "Neighbor verification", local_body: "Local body deed verification",
  land_rate: "Land rate proof", khasra: "Khasra Milan / map",
};
export const PHOTO_VIEW_ROLES = ["admin", "office"];
export const LOAN_LEAD_VIEW_ROLES = ["admin"];
export const tagLabel = (tag) => PHOTO_TAGS.find((t) => t.value === tag)?.label || "Other";
export const labelOf = (list, value) => list.find((x) => x.value === value)?.label || value || "—";

const filled = (v) => String(v ?? "").trim().length > 0;
const orEmpty = (v) => (v == null ? "" : v);
const newKey = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `k${Date.now()}${Math.random().toString(16).slice(2)}`);
export const emptyLoanLead = () => ({ _key: newKey(), customer_name: "", phone: "", loan_amount_inr: "", telecalling_verified: false });

export function emptyVisitForm(lead) {
  const v = lead.visit || {};
  const nb = v.neighbor || {};
  const lr = v.land_rate || {};
  const leads = v.loan_leads?.length ? v.loan_leads : Array.from({ length: MIN_LOAN_LEADS }, emptyLoanLead);
  return {
    existing_loan_leads: v.loan_leads?.length ? 0 : (v.loan_leads_count || 0),
    property_address: orEmpty(v.property_address ?? lead.property_address),
    owner_name: orEmpty(v.owner_name ?? lead.customer_name),
    property_type: orEmpty(v.property_type),
    floors: orEmpty(v.floors),
    construction_type: orEmpty(v.construction_type),
    boundary_north: orEmpty(v.boundary_north), boundary_south: orEmpty(v.boundary_south),
    boundary_east: orEmpty(v.boundary_east), boundary_west: orEmpty(v.boundary_west),
    plot_length_ft: orEmpty(v.plot_length_ft), plot_width_ft: orEmpty(v.plot_width_ft),
    built_up_area_sqft: orEmpty(v.built_up_area_sqft), year_of_construction: orEmpty(v.year_of_construction),
    bylaws_notes: orEmpty(v.bylaws_notes), valuation_amount_inr: orEmpty(v.valuation_amount_inr),
    plot_land_rate_per_sqft: orEmpty(v.plot_land_rate_per_sqft), construction_cost_per_sqft: orEmpty(v.construction_cost_per_sqft),
    valuation_manual: v.valuation_manual ?? (v.valuation_amount_inr != null),
    neighbor: { name: orEmpty(nb.name), phone: orEmpty(nb.phone), address: orEmpty(nb.address), local_body_type: orEmpty(nb.local_body_type),
      neighbor_photo_ids: nb.neighbor_photo_ids || [], local_body_photo_ids: nb.local_body_photo_ids || [] },
    land_rate: { source_type: lr.source_type || "broker", source_name: orEmpty(lr.source_name), source_phone: orEmpty(lr.source_phone), photo_ids: lr.photo_ids || [] },
    khasra_photo_ids: v.khasra_photo_ids || [],
    loan_leads: leads.map((l) => ({ ...emptyLoanLead(), ...l, loan_amount_inr: orEmpty(l.loan_amount_inr) })),
    property_photos: v.property_photos?.length ? v.property_photos : (v.photo_ids || []).map((id) => ({ file_id: id, tag: "other" })),
  };
}

export function mergeDraft(base, draft) {
  if (!draft) return base;
  return {
    ...base, ...draft,
    neighbor: { ...base.neighbor, ...(draft.neighbor || {}) },
    land_rate: { ...base.land_rate, ...(draft.land_rate || {}) },
    loan_leads: draft.loan_leads?.length ? draft.loan_leads : base.loan_leads,
    property_photos: draft.property_photos || base.property_photos,
  };
}

export const getPath = (obj, path) => path.split(".").reduce((o, k) => (o == null ? undefined : o[k]), obj);
export function setPath(obj, path, value) {
  const [head, ...rest] = path.split(".");
  return rest.length ? { ...obj, [head]: setPath(obj[head] || {}, rest.join("."), value) } : { ...obj, [head]: value };
}

/* Valuation = (plot length × width × land rate) + (built-up area × construction cost); mirrors visit_rules.valuation_breakdown. */
export const VALUATION_INPUTS = ["plot_length_ft", "plot_width_ft", "plot_land_rate_per_sqft", "built_up_area_sqft", "construction_cost_per_sqft"];
const numOrNull = (v) => (v === "" || v == null || Number.isNaN(Number(v)) ? null : Number(v));
const mul = (a, b) => (a == null || b == null ? null : a * b);
export const formatINR = (v) => (v == null ? "—" : `₹${Math.round(Number(v)).toLocaleString("en-IN")}`);
export const formatSqft = (v) => (v == null ? "—" : `${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })} sq ft`);
export function valuationBreakdown(f) {
  const plot_area_sqft = mul(numOrNull(f.plot_length_ft), numOrNull(f.plot_width_ft));
  const plot_value_inr = mul(plot_area_sqft, numOrNull(f.plot_land_rate_per_sqft));
  const construction_value_inr = mul(numOrNull(f.built_up_area_sqft), numOrNull(f.construction_cost_per_sqft));
  const parts = [plot_value_inr, construction_value_inr].filter((x) => x != null);
  const computed_valuation_inr = parts.length ? Math.round(parts.reduce((a, b) => a + b, 0)) : null;
  return { plot_area_sqft, plot_value_inr, construction_value_inr, computed_valuation_inr };
}
export function applyValuation(f) {
  if (f.valuation_manual) return f;
  const { computed_valuation_inr } = valuationBreakdown(f);
  return { ...f, valuation_amount_inr: computed_valuation_inr == null ? "" : String(computed_valuation_inr) };
}

export function sectionFiles(form, section) {
  const arr = getPath(form, SECTION_PATHS[section]) || [];
  return section === "property" ? arr : arr.map((id) => ({ file_id: id, tag: null }));
}
export function addSectionFile(form, section, file_id, tag = "other") {
  const path = SECTION_PATHS[section];
  const cur = getPath(form, path) || [];
  return setPath(form, path, section === "property" ? [...cur, { file_id, tag }] : [...cur, file_id]);
}
export function removeSectionFile(form, section, file_id) {
  const path = SECTION_PATHS[section];
  const cur = getPath(form, path) || [];
  return setPath(form, path, cur.filter((x) => (section === "property" ? x.file_id : x) !== file_id));
}
export function retagPhoto(form, file_id, tag) {
  return { ...form, property_photos: form.property_photos.map((p) => (p.file_id === file_id ? { ...p, tag } : p)) };
}

const CHECKS = {
  basic: (f) => [
    !filled(f.property_address) && "Property address is required",
    !filled(f.owner_name) && "Owner name is required",
    !filled(f.property_type) && "Property type is required",
  ],
  neighbor: (f) => [
    !(filled(f.neighbor.name) && filled(f.neighbor.phone)) && "Neighbor name and phone number are required",
    !f.neighbor.neighbor_photo_ids.length && "Neighbor verification photo is required",
    !f.neighbor.local_body_photo_ids.length && "Panchayat / Nagar Parishad / Nagar Palika deed verification photo is required",
  ],
  land_rate: (f) => [
    !(filled(f.land_rate.source_name) && filled(f.land_rate.source_phone)) && "Land rate source name and phone are required",
    !f.land_rate.photo_ids.length && "Land rate verification photo or document is required",
  ],
  khasra: (f) => [!f.khasra_photo_ids.length && "Khasra Milan / map alignment document is required"],
  leads: (f) => {
    const done = f.loan_leads.filter((l) => filled(l.customer_name) && filled(l.phone));
    const partial = f.loan_leads.filter((l) => (filled(l.customer_name) || filled(l.phone)) && !(filled(l.customer_name) && filled(l.phone))).length;
    const unverified = done.filter((l) => !l.telecalling_verified).length;
    const total = done.length + (f.existing_loan_leads || 0);
    return [
      total < MIN_LOAN_LEADS && `At least ${MIN_LOAN_LEADS} loan leads with name and phone are required (${total} entered)`,
      partial > 0 && `${partial} loan lead row(s) are missing a name or phone`,
      unverified > 0 && `${unverified} loan lead(s) still need telecalling confirmation`,
    ];
  },
  photos: (f) => [
    f.property_photos.length < MIN_PROPERTY_PHOTOS && `At least ${MIN_PROPERTY_PHOTOS} property photos are required (${f.property_photos.length} uploaded)`,
  ],
};
export const stepIssues = (form, step) => CHECKS[step](form).filter(Boolean);
export const allIssues = (form) => STEPS.flatMap((s) => stepIssues(form, s.key));

const num = (v) => (v === "" || v == null ? null : Number(v));
export function toPayload(form) {
  const { existing_loan_leads, ...rest } = form;
  return {
    ...rest,
    floors: num(form.floors), plot_length_ft: num(form.plot_length_ft), plot_width_ft: num(form.plot_width_ft),
    built_up_area_sqft: num(form.built_up_area_sqft), year_of_construction: num(form.year_of_construction),
    valuation_amount_inr: num(form.valuation_amount_inr),
    plot_land_rate_per_sqft: num(form.plot_land_rate_per_sqft), construction_cost_per_sqft: num(form.construction_cost_per_sqft),
    valuation_manual: Boolean(form.valuation_manual),
    loan_leads: form.loan_leads.filter((l) => filled(l.customer_name) || filled(l.phone)).map(({ _key, ...l }) => ({ ...l, loan_amount_inr: num(l.loan_amount_inr) })),
    photo_ids: form.property_photos.map((p) => p.file_id),
    document_ids: [],
  };
}
